import { LocalizationString, WarningIcon } from '@celito.clients/assets';
import { ButtonTypes, FormEngineModeTypeEnum } from '@celito.clients/enums';
import { useURLParams } from '@celito.clients/hooks';
import { ListView } from '@celito.clients/list-view-engine';
import { UserContext } from '@celito.clients/provider';
import {
  CustomButton,
  FormActionButtonsContainer,
} from '@celito.clients/shared';
import { useContext } from 'react';
import { useFormContext } from 'react-hook-form';

import { ConfirmButton } from '../../components/wizard-container/confirm-button/confirm-button.component';
import { useFormEngineContext } from '../../hooks/useFormEngineContext';
import { getFormActionsVisibilityRules } from '../../utils/helper';
import { RelatedOjectsViewProps } from './related-objects.model';
import classes from './related-objects.module.css';

export const RelatedObjectsView = ({
  isLoading,
  attributeConfig,
  recordDetails,
  isSubmitting,
  viewDto,
  onSave,
  showFormActions = true,
  className,
  recordfilter,
  pageComponent,
  ...props
}: RelatedOjectsViewProps): JSX.Element => {
  const [, viewName] = pageComponent ? pageComponent.split('/') : ['', ''];
  const { user } = useContext(UserContext);
  const { isAllSectionsValid, fieldsApiErrors } = useFormEngineContext();

  const isSubmitDisabled =
    !isAllSectionsValid ||
    isLoading ||
    isSubmitting ||
    (fieldsApiErrors?.fields?.length ?? 0) > 0;

  const { urlParams } = useURLParams();
  const {
    formState: { isDirty },
  } = useFormContext();
  const requestType = urlParams.get('requestType');
  const isUpdateRequest = requestType?.includes('Update');
  const isEditAction = !isUpdateRequest;

  const showSubmitConfirmationPopup = Boolean(
    viewDto?.view?.[0]?.showSubmitConfirmationPopup
  );

  const submitConfirmationPopupMessage =
    viewDto?.view?.[0]?.submitConfirmationPopupMessage;

  const { showSaveButton, showSubmitButton } = getFormActionsVisibilityRules(
    attributeConfig?.lifecycleStageGroups,
    recordDetails?.lifecycleStage as string,
    isEditAction ? 'edit' : 'update',
    props.mode
  );

  // Get parent record label and name for dynamic relationship population
  const parentRecordLabel = recordDetails?.['label'] as string;
  const parentRecordName = recordDetails?.['name'] as string;

  // Get effective record roles by checking if the logged-in user has the role
  const getEffectiveRecordRoles = () => {
    if (!attributeConfig?.objectAttributeDefinitions || !user || !recordDetails)
      return [];
    const effectiveRoles: string[] = [];

    for (const attr of attributeConfig.objectAttributeDefinitions) {
      if (attr.recordRole && attr.key) {
        // Check if the field value matches the logged-in user's ID
        const fieldValue = recordDetails[attr.key];
        if (fieldValue) {
          // Handle both single and multi-select reference fields
          const values = Array.isArray(fieldValue) ? fieldValue : [fieldValue];
          if (values.some((value) => value.name === user.name)) {
            if (!effectiveRoles.includes(attr?.recordRole as string)) {
              effectiveRoles.push(attr?.recordRole as string);
            }
          }
        }
      }
    }

    // If the logged-in user is a delegate on the task, add the task's recordRole
    if (
      props.task?.recordRole &&
      props.task?.delegates?.some((delegate) => delegate.name === user.name) &&
      !effectiveRoles.includes(props.task.recordRole)
    ) {
      effectiveRoles.push(props.task.recordRole);
    }

    return effectiveRoles;
  };

  return (
    <>
      <div className={classes.container}>
        <div className={classes.innerContainer}>
          <ListView
            key={viewName}
            viewName={viewName}
            dontSetHeader={true}
            objectName={props.objectName}
            recordName={props.recordName}
            showAddButton={false}
            recordVersion={props.recordData?.version as string}
            parentRecordRoles={getEffectiveRecordRoles()}
            parentObjectName={props.objectName}
            parentRecordLabel={parentRecordLabel}
            parentRecordName={parentRecordName}
          />
        </div>
      </div>
      {showFormActions && (
        <FormActionButtonsContainer>
          <CustomButton
            buttonTitle={LocalizationString.CANCEL}
            buttonType={ButtonTypes.Ghost}
            onClick={props.onCancel}
          />
          <div className={classes.formActionBtns}>
            <CustomButton
              buttonTitle={LocalizationString.PREVIOUS}
              leftIcon="Previous"
              buttonType={ButtonTypes.Ghost}
              onClick={props.goToPreviousStep}
            />
            {props.step < props.totalSteps - 1 && (
              <CustomButton
                buttonTitle={LocalizationString.NEXT}
                leftIcon="Next"
                buttonType={ButtonTypes.Ghost}
                onClick={props.goToNextStep}
              />
            )}

            {props.mode === FormEngineModeTypeEnum.Edit && showSaveButton && (
              <CustomButton
                buttonTitle="Save"
                leftIcon="Save"
                buttonType={ButtonTypes.Secondary}
                isLoading={isLoading}
                disabled={isLoading || isSubmitting || !isDirty}
                onSubmit={
                  !isLoading
                    ? () => onSave?.(false) as Promise<void>
                    : undefined
                }
              />
            )}
            {showSubmitButton && props.step === props.totalSteps - 1 && (
              <ConfirmButton
                buttonTitle={'Submit'}
                leftIcon="Submit"
                buttonType={ButtonTypes.Primary}
                isLoading={isSubmitting}
                disabled={isSubmitDisabled}
                onSubmit={
                  !isSubmitting
                    ? () => onSave?.(true) as Promise<void>
                    : undefined
                }
                showSubmitConfirmationPopup={showSubmitConfirmationPopup}
                dialogProps={{
                  primaryButtonText: LocalizationString.SUBMIT,
                  title: LocalizationString.SUBMIT,
                  description: submitConfirmationPopupMessage,
                  secondaryButtonText: LocalizationString.CANCEL,
                  iconSrc: WarningIcon,
                }}
              />
            )}
          </div>
        </FormActionButtonsContainer>
      )}
    </>
  );
};
